import React from 'react';
import { createRoot } from 'react-dom/client';
import { redirect } from 'redux-first-router';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/lib/integration/react';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './api/queryClient';
import { configureStore } from './store';
import { LocalizationProvider } from './localization/LocalizationProvider';
import { App } from './components/App/App';
import { logout } from './store/actions/user';
import { REQUEST_STATUSES } from './api/requests/requestStatusConstants';
import { PAGES } from './utils/pages';
import { MuiThemeProvider } from './themes/toggleTheme';

import './themes/customs.css';

declare global {
    interface Window {
        mainStore: any;
    }
}

const { store, persistor } = configureStore();

queryClient.setDefaultOptions({
    queries: {
        retry: (failureCount, err: any) => {
            const error = err?.response;
            if (error?.status === undefined) {
                throw err;
            }

            if (
                error.status === REQUEST_STATUSES.UNAUTHORIZED ||
                (error.status === REQUEST_STATUSES.FORBIDDEN && error.data.error === 'Authorization failed')
            ) {
                store.dispatch(redirect({ type: PAGES.LOGIN }));
                store.dispatch(logout());

                return false;
            }

            return true;
        }
    }
});

console.debug('NODE_ENV:', process.env.NODE_ENV);
console.debug('APP_ENV:', process.env.APP_ENV);
console.debug('APP_VERSION:', process.env.APP_VERSION);

window.mainStore = store;

const container = document.getElementById('main');
if (container != null) {
    const root = createRoot(container);
    root.render(
        <QueryClientProvider client={queryClient}>
            <DndProvider backend={HTML5Backend}>
                <PersistGate persistor={persistor}>
                    <Provider store={store}>
                        <LocalizationProvider>
                            <MuiThemeProvider>
                                <App />
                                {/*<ReactQueryDevtools initialIsOpen={false} buttonPosition="relative" />*/}
                            </MuiThemeProvider>
                        </LocalizationProvider>
                    </Provider>
                </PersistGate>
            </DndProvider>
        </QueryClientProvider>
    );
}

// Commented out because it slows down internet explorer dramatically
// if (process.env.NODE_ENV !== ENV.PRODUCTION && !!document.documentMode !== true) {
//     whyDidYouRender(React);
// }
